// src/contexts/AuthContext.js - VERSÃO HÍBRIDA (LOCAL + EXTERNO)
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const AuthContext = createContext();

export const NIVEIS = { EXPEDICAO: 1, LABORATORIO: 2, ADMINISTRADOR: 3 };

// ✅ CONFIGURAÇÃO HÍBRIDA - AUTOMÁTICA
const getApiBaseUrl = () => {
  // 1. Verificar variável de ambiente primeiro
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2. Auto-detectar baseado no hostname atual
  const hostname = window.location.hostname;

  // 3. Configurações por ambiente
  if (hostname === 'cienciamagistral.pharmacloudbr.com.br') {
    return 'https://api-cienciamagistral.pharmacloudbr.com.br/api';
  } else {
    // Fallback para localhost se REACT_APP_API_URL não estiver definido
    return `http://${hostname}:3003/api`;
  }
};

const API_BASE_URL = getApiBaseUrl();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ SISTEMA DE HEALTH CHECK
  const [apiStatus, setApiStatus] = useState('checking');

  const login = async (usuario, senha) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario, senha }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // Corrigir extração dos dados do backend
      if (data.success && data.dados && data.dados.user && data.dados.token) {
        if (data.dados.user.trocar_senha) {
          // Troca de senha obrigatória: NÃO completa o login. O token
          // temporário serve só para chamar /auth/change-password.
          setApiStatus('connected');
          return { success: false, trocarSenha: true, token: data.dados.token };
        }
        const userDataWithToken = { ...data.dados.user, token: data.dados.token };
        setUser(userDataWithToken);
        localStorage.setItem('user', JSON.stringify(userDataWithToken));
        setApiStatus('connected');
        return { success: true };
      }
      setApiStatus('error');
      return { success: false, message: data.message };
    } catch (error) {
      console.error('❌ Erro no login:', error);
      setApiStatus('error');
      
      // Mensagens de erro mais específicas
      if (error.name === 'AbortError') {
        return { success: false, message: 'Timeout: Servidor não respondeu em 10 segundos' };
      } else if (error.message.includes('Failed to fetch')) {
        return { success: false, message: 'Erro de conexão: Verifique se o servidor está rodando' };
      } else {
        return { success: false, message: `Erro de conexão: ${error.message}` };
      }
    }
  };

  // Estavel: so mexe em setters, que o React garante constantes.
  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    setApiStatus('disconnected');
  }, []);

  // Conclui o fluxo "trocar senha no próximo login": troca a senha usando o
  // token temporário do login e entra de novo já com a senha nova.
  const concluirTrocaSenha = async (usuario, senhaAtual, novaSenha, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ usuario, senhaAtual, novaSenha })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        return { success: false, message: data.message || 'Erro ao trocar a senha' };
      }
      return await login(usuario, novaSenha);
    } catch (error) {
      return { success: false, message: `Erro de conexão: ${error.message}` };
    }
  };

  const hasPermission = (nivelRequerido) => user ? user.nivel >= nivelRequerido : false;

  const apiCall = useCallback(async (endpoint, options = {}) => {
    let currentEndpoint = endpoint;
    let url = `${API_BASE_URL}${currentEndpoint}`;
    const storedUser = localStorage.getItem('user');
    const token = storedUser ? JSON.parse(storedUser).token : null;
    const authHeader = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    
    try {
      let response = await fetch(url, { 
        ...options, 
        headers: { 
          'Content-Type': 'application/json', 
          ...options.headers, 
          ...authHeader 
        } 
      });
      
      // Fallback automático em desenvolvimento: tentar rotas antigas se -new não existir
      if (!response.ok && response.status === 404) {
        const isRelatoriosNew = currentEndpoint.startsWith('/relatorios-new');
        const isProdutividadeNew = currentEndpoint.startsWith('/produtividade-new');
        if (isRelatoriosNew || isProdutividadeNew) {
          const legacyEndpoint = isRelatoriosNew
            ? currentEndpoint.replace('/relatorios-new', '/relatorios')
            : currentEndpoint.replace('/produtividade-new', '/produtividade');
          const legacyUrl = `${API_BASE_URL}${legacyEndpoint}`;
          console.warn(`↩️  404 em ${currentEndpoint}. Tentando fallback: ${legacyEndpoint}`);
          response = await fetch(legacyUrl, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              ...options.headers,
              ...authHeader
            }
          });
          currentEndpoint = legacyEndpoint;
          url = legacyUrl;
        }
      }

      if (!response.ok) {
        // 🔒 Sessão expirada / token inválido → desloga e volta para o login automaticamente
        if (response.status === 401) {
          console.warn('🔒 401 (token inválido/expirado): encerrando sessão e voltando para o login.');
          logout();
          throw new Error('Sessão expirada. Faça login novamente.');
        }
        const errorData = await response.json().catch(() => ({
          message: `Erro HTTP! Status: ${response.status}`
        }));
        throw new Error(errorData.message);
      }
      
      setApiStatus('connected');
      return response.json();
    } catch (error) {
      console.error(`❌ Erro na API ${endpoint}:`, error);
      setApiStatus('error');
      throw error;
    }
  }, [logout]);

  // ✅ Helper: tenta endpoint primário e, se 404, tenta fallback
  const apiCallWithFallback = useCallback(async (primaryEndpoint, fallbackEndpoint, options = {}) => {
    try {
      return await apiCall(primaryEndpoint, options);
    } catch (error) {
      const msg = String(error?.message || '');
      const is404 = msg.includes('404') || msg.includes('não foi encontrada');
      if (is404 && fallbackEndpoint) {
        console.warn(`404 em ${primaryEndpoint}. Tentando fallback: ${fallbackEndpoint}`);
        return await apiCall(fallbackEndpoint, options);
      }
      throw error;
    }
  }, [apiCall]);

  // ✅ HEALTH CHECK DA API
  const checkApiHealth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setApiStatus('connected');
        return true;
      } else {
        setApiStatus('error');
        return false;
      }
    } catch (error) {
      setApiStatus('error');
      return false;
    }
  };

  // Identidade estavel: e o que impede o duplo fetch e o laco de
  // requisicoes quando apiStatus alterna entre connected e error.
  const api = useMemo(() => ({
    // ============================================================================
    // LINHA DO TEMPO
    // ============================================================================
    buscarLinhaTempo: (termo) => apiCall(`/linha-tempo/${termo}`),

    // ============================================================================
    // FUNCIONÁRIOS
    // ============================================================================
    listarFuncionarios: () => apiCall('/funcionarios'),
    criarFuncionario: (dados) => apiCall('/funcionarios', { 
      method: 'POST', 
      body: JSON.stringify(dados) 
    }),
    atualizarFuncionario: (id, dados) => apiCall(`/funcionarios/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(dados) 
    }),
    excluirFuncionario: (id) => apiCall(`/funcionarios/${id}`, { 
      method: 'DELETE' 
    }),

    // ============================================================================
    // SETORES
    // ============================================================================
    listarSetores: () => apiCall('/setores'),
    criarSetor: (dados) => apiCall('/setores', { 
      method: 'POST', 
      body: JSON.stringify(dados) 
    }),
    atualizarSetor: (id, dados) => apiCall(`/setores/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(dados) 
    }),
    excluirSetor: (id) => apiCall(`/setores/${id}`, {
      method: 'DELETE'
    }),

    // ============================================================================
    // CONFIGURAÇÃO — travas antifraude
    // ============================================================================
    listarParametros: () => apiCall('/configuracoes/parametros'),
    atualizarParametro: (chave, valor) => apiCall(`/configuracoes/parametros/${chave}`, {
      method: 'PUT',
      body: JSON.stringify({ valor })
    }),

    listarEtapas: () => apiCall('/configuracoes/etapas'),
    atualizarEtapa: (id, dados) => apiCall(`/configuracoes/etapas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados)
    }),

    // Aba Parametros - trava A (forma farmaceutica x etapa)
    listarFormasFarmaceuticas: () => apiCall('/configuracoes/formas-farmaceuticas'),
    listarFormasIncompativeis: () => apiCall('/configuracoes/formas-incompativeis'),
    criarFormaIncompativel: (dados) => apiCall('/configuracoes/formas-incompativeis', {
      method: 'POST',
      body: JSON.stringify(dados)
    }),
    atualizarFormaIncompativel: (tpf, dados) => apiCall(`/configuracoes/formas-incompativeis/${tpf}`, {
      method: 'PUT',
      body: JSON.stringify(dados)
    }),
    alternarFormaIncompativel: (tpf, ativo) => apiCall(`/configuracoes/formas-incompativeis/${tpf}/ativo`, {
      method: 'PUT',
      body: JSON.stringify({ ativo })
    }),
    excluirFormaIncompativel: (tpf) => apiCall(`/configuracoes/formas-incompativeis/${tpf}`, {
      method: 'DELETE'
    }),

    // Aba Parametros - trava B (ordem entre etapas).
    // encodeURIComponent no gatilho: e texto livre de 40 chars, diferente do
    // codigo numerico usado em funcionario-setores.
    listarEtapasIncompativeis: () => apiCall('/configuracoes/etapas-incompativeis'),
    criarEtapaIncompativel: (dados) => apiCall('/configuracoes/etapas-incompativeis', {
      method: 'POST',
      body: JSON.stringify(dados)
    }),
    atualizarEtapaIncompativel: (gatilho, dados) =>
      apiCall(`/configuracoes/etapas-incompativeis/${encodeURIComponent(gatilho)}`, {
        method: 'PUT',
        body: JSON.stringify(dados)
      }),
    alternarEtapaIncompativel: (gatilho, ativo) =>
      apiCall(`/configuracoes/etapas-incompativeis/${encodeURIComponent(gatilho)}/ativo`, {
        method: 'PUT',
        body: JSON.stringify({ ativo })
      }),
    excluirEtapaIncompativel: (gatilho) =>
      apiCall(`/configuracoes/etapas-incompativeis/${encodeURIComponent(gatilho)}`, {
        method: 'DELETE'
      }),

    simularIncompativeis: (meses = 6) =>
      apiCall(`/configuracoes/incompativeis/simulacao?meses=${meses}`),

    listarFuncionarioSetores: () => apiCall('/configuracoes/funcionario-setores'),
    atualizarFuncionarioSetores: (codigo, setores) => apiCall(`/configuracoes/funcionario-setores/${codigo}`, {
      method: 'PUT',
      body: JSON.stringify({ setores })
    }),

    listarTerminais: () => apiCall('/configuracoes/terminais'),
    listarTerminaisDescobertos: () => apiCall('/configuracoes/terminais/descobertos'),
    criarTerminal: (dados) => apiCall('/configuracoes/terminais', {
      method: 'POST',
      body: JSON.stringify(dados)
    }),
    atualizarTerminal: (id, dados) => apiCall(`/configuracoes/terminais/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados)
    }),
    excluirTerminal: (id) => apiCall(`/configuracoes/terminais/${id}`, {
      method: 'DELETE'
    }),

    listarLogsAuditoria: (filtros = {}) => {
      const qs = new URLSearchParams(
        Object.entries(filtros).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      ).toString();
      return apiCall(`/configuracoes/logs${qs ? '?' + qs : ''}`);
    },
    resumoLogsAuditoria: (filtros = {}) => {
      const qs = new URLSearchParams(
        Object.entries(filtros).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      ).toString();
      return apiCall(`/configuracoes/logs/resumo${qs ? '?' + qs : ''}`);
    },

    // ============================================================================
    // USUÁRIOS
    // ============================================================================
    listarUsuarios: () => apiCall('/usuarios'),
    criarUsuario: (dados) => apiCall('/usuarios', { 
      method: 'POST', 
      body: JSON.stringify(dados) 
    }),
    atualizarUsuario: (id, dados) => apiCall(`/usuarios/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(dados) 
    }),
    excluirUsuario: (id) => apiCall(`/usuarios/${id}`, { 
      method: 'DELETE' 
    }),

    // ============================================================================
    // PRODUTIVIDADE
    // ============================================================================
    produtividadeGeral: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.periodo) queryParams.append('periodo', params.periodo);
      
      const endpoint = '/produtividade/geral' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      return apiCall(endpoint);
    },

    // Funcionários por setor (case-insensitive) com período
    setorFuncionarios: (params = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/produtividade/setor/funcionarios' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      return apiCall(endpoint);
    },

    produtividadeFuncionariosMes: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      // Adicionar filtros se fornecidos
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/relatorios-new/produtividade-funcionarios' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    produtividadeFuncionario: (codigo, params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.periodo) queryParams.append('periodo', params.periodo);
      
      const endpoint = `/produtividade/funcionario/${codigo}` +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // ============================================================================
    // ABA LABORATÓRIO - MÉTODOS
    // ============================================================================

    // Produtividade por setores do laboratório
    laboratorioSetores: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/laboratorio/setores' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Produção geral do laboratório
    laboratorioProducaoGeral: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/laboratorio/producao-geral' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Horários de pico do laboratório
    laboratorioHorariosPico: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/laboratorio/horarios-pico' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Crescimento vs mês passado
    laboratorioCrescimento: () => apiCall('/produtividade/laboratorio/crescimento'),

    // ============================================================================
    // ABA FUNCIONÁRIOS - MÉTODOS
    // ============================================================================

    // Top 5 funcionários
    funcionariosTop5: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/top5' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Métricas detalhadas de funcionários
    funcionariosMetricas: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/metricas' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Tempo entre leituras por funcionário
    funcionariosTempoEntreLeituras: (funcionario, params = {}) => {
      const queryParams = new URLSearchParams();
      queryParams.append('funcionario', funcionario);
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/tempo-entre-leituras' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Horários de pico por funcionário
    funcionariosHorariosPico: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/funcionarios/horarios-pico' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // ============================================================================
    // CAPACIDADE LAB
    // ============================================================================
    capacidadeLab: async (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.codigos) queryParams.append('codigos', params.codigos);
      const sufixo = queryParams.toString() ? `?${queryParams.toString()}` : '';
      try {
        return await apiCall('/capacidade' + sufixo);
      } catch (e) {
        try {
          return await apiCall('/capacidade/lab' + sufixo);
        } catch (e2) {
          return apiCall('/capacidade-lab' + sufixo);
        }
      }
    },

    // ============================================================================
    // ABA SETOR - MÉTODOS
    // ============================================================================

    // Métricas detalhadas por setor
    setoresMetricas: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/setores/metricas' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // Comparativo entre setores
    setoresComparativo: (params = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/produtividade/setores/comparativo' +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');

      return apiCall(endpoint);
    },

    // ============================================================================
    // REQUISIÇÕES
    // ============================================================================
    listarRequisicoes: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/requisicoes' + (queryParams.toString() ? `?${queryParams.toString()}` : '');
      return apiCall(endpoint);
    },
    buscarRequisicao: (id) => apiCall(`/requisicoes/${id}`),

    // Painel expandido da tela de Requisicoes. Sao dois metodos e nao um de
    // proposito: o primeiro so toca o SQL Server e responde em milissegundos,
    // o segundo depende do Firebird e pode demorar ou falhar. A tela chama os
    // dois com Promise.allSettled para que a ficha indisponivel nao apague a
    // trilha nem a auditoria.
    detalhesRequisicao: (id) => apiCall(`/requisicoes/${id}/detalhes`),
    fichaRequisicaoFirebird: (id) => apiCall(`/requisicoes/${id}/firebird`),

    // ============================================================================
    // FC12100 (Firebird – tabela de origem das requisições)
    // ============================================================================
    listarFC12100: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/fc12100' + (queryParams.toString() ? `?${queryParams.toString()}` : '');
      return apiCall(endpoint);
    },
    saudeFC12100: () => apiCall('/fc12100/health'),

    // ============================================================================
    // PRODUÇÃO – Kanban (FC12100 + REQUISICOES + controle_envio)
    // ============================================================================
    kanbanProducao: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/producao/kanban' + (queryParams.toString() ? `?${queryParams.toString()}` : '');
      return apiCall(endpoint);
    },
    // Controle de Ficha — mesmos filtros do Kanban; divide em Impressos / Não impressos
    // conforme exista registro de pesagem em FC1A000.
    controleFicha: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/producao/controle-ficha' + (queryParams.toString() ? `?${queryParams.toString()}` : '');
      return apiCall(endpoint);
    },
    listarFormasFarma: () => apiCall('/producao/formas-farma'),
    // Modal de fórmula do Kanban: chaveado direto pela chave do Firebird,
    // porque o card nasce da FC12100 e não tem id de bipe no SQL Server.
    fichaProducao: (cdfil, nrrqu, serier) =>
      apiCall(`/producao/ficha/${cdfil}/${nrrqu}/${encodeURIComponent(serier)}`),
    listarTiposQuantidade: () => apiCall('/requisicoes/tipos'),

    // Ordem de producao: a lista sai da ORDEMPRODUCAO (SQL Server) e o detalhe
    // expandido vai direto na FC18000 do Firebird.
    listarOrdemProducao: (filtros = {}) => {
      const qs = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined && v !== false) qs.append(k, v);
      });
      const q = qs.toString();
      return apiCall('/ordem-producao' + (q ? `?${q}` : ''));
    },
    listarFiltrosOrdemProducao: () => apiCall('/ordem-producao/filtros'),
    detalheOrdemFirebird: (cdfil, nrord) =>
      apiCall(`/ordem-producao/${cdfil}/${nrord}/firebird`),
    leiturasDaOrdem: (cdfil, nrord) =>
      apiCall(`/ordem-producao/${cdfil}/${nrord}/leituras`),
    excluirLeituraOrdem: (id) => apiCall(`/ordem-producao/${id}`, { method: 'DELETE' }),
    atualizarRequisicao: (id, dados) => apiCall(`/requisicoes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados)
    }),
    excluirRequisicao: (id) => apiCall(`/requisicoes/${id}`, {
      method: 'DELETE'
    }),
    estatisticasRequisicoes: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const endpoint = '/requisicoes/stats/resumo' + (queryParams.toString() ? `?${queryParams.toString()}` : '');
      return apiCall(endpoint);
    },

    // ============================================================================
    // MÉTODOS AUXILIARES PARA FILTROS
    // ============================================================================

    // Buscar opções para filtros (setores e funcionários)
    buscarOpcoesFiltros: () => {
      return Promise.all([
        apiCall('/setores'),
        apiCall('/funcionarios')
      ]).then(([setoresResponse, funcionariosResponse]) => {
        return {
          setores: setoresResponse?.dados || [],
          funcionarios: funcionariosResponse?.dados || []
        };
      }).catch(() => {
        return { setores: [], funcionarios: [] };
      });
    },

    // ============================================================================
    // RELATÓRIOS
    // ============================================================================
    relatorioLeituras: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const endpoint = '/relatorios-new/leituras' + 
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      
      return apiCall(endpoint);
    },

    relatorioTabular: (filtros = {}) => {
      const queryParams = new URLSearchParams();
      
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/tabular' + qs;
      const fallback = '/relatorios/tabular' + qs;

      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioTabularValidar: (filtros = {}) => {
      const queryParams = new URLSearchParams();

      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/tabular/validar' + qs;
      const fallback = '/relatorios/tabular/validar' + qs;

      return apiCallWithFallback(endpoint, fallback);
    },

    relatorioCapsulas: (filtros = {}) => {
      const queryParams = new URLSearchParams();

      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/capsulas' + qs;

      return apiCall(endpoint);
    },

    relatorioSaches: (filtros = {}) => {
      const queryParams = new URLSearchParams();

      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios/saches' + qs;

      return apiCall(endpoint);
    },

    relatorioProducaoOrdem: (filtros = {}) => {
      const queryParams = new URLSearchParams();

      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const endpoint = '/relatorios-new/producao-ordem' + qs;

      return apiCall(endpoint);
    },

    // ============================================================================
    // CONFERÊNCIA FARMA
    // ============================================================================
    listarFiliais: () => apiCall('/filiais'),

    conferenciaFarmaListar: (filtros = {}) => {
      const qp = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qp.append(k, v);
      });
      const qs = qp.toString() ? `?${qp.toString()}` : '';
      return apiCall(`/conferencia-farma/listar${qs}`);
    },
    conferenciaFarmaImagens: (nrrqu, filialId) =>
      apiCall(`/conferencia-farma/${nrrqu}/${filialId}/imagens`),
    conferenciaFarmaStatus: (nrrqu, filialId) =>
      apiCall(`/conferencia-farma/${nrrqu}/${filialId}/status`),
    conferenciaFarmaConferir: (nrrqu, filialId) =>
      apiCall(`/conferencia-farma/${nrrqu}/conferir`, {
        method: 'POST',
        body: JSON.stringify({ filial_id: filialId })
      }),
    conferenciaFarmaReverter: (nrrqu, filialId, divergenciaId = null) =>
      apiCall(`/conferencia-farma/${nrrqu}/reverter-conferencia`, {
        method: 'POST',
        body: JSON.stringify({ filial_id: filialId, divergencia_id: divergenciaId })
      }),
    conferenciaFarmaEstatisticas: (filialId) =>
      apiCall(`/conferencia-farma/estatisticas/${filialId}`),
    conferenciaFarmaRelatorioProducao: (filtros = {}) => {
      const qp = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qp.append(k, v);
      });
      const qs = qp.toString() ? `?${qp.toString()}` : '';
      return apiCall(`/conferencia-farma/relatorio-producao${qs}`);
    },
    conferenciaFarmaRelatorioDivergencias: (filtros = {}) => {
      const qp = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qp.append(k, v);
      });
      const qs = qp.toString() ? `?${qp.toString()}` : '';
      return apiCall(`/conferencia-farma/relatorio-divergencias${qs}`);
    },
    conferenciaFarmaDebug: (nrrqu) => apiCall(`/conferencia-farma/debug/${nrrqu}`),

    // ============================================================================
    // DIVERGÊNCIAS
    // ============================================================================
    divergenciasListar: (filialId, filtros = {}) => {
      const qp = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qp.append(k, v);
      });
      const qs = qp.toString() ? `?${qp.toString()}` : '';
      return apiCall(`/divergencias/filial/${filialId}${qs}`);
    },
    divergenciasCount: (filialId) =>
      apiCall(`/divergencias/filial/${filialId}/count`),
    divergenciasResumo: (filialId) => {
      const qs = filialId ? `?filial_id=${filialId}` : '';
      return apiCall(`/divergencias/stats/resumo${qs}`);
    },
    divergenciasHistoricoReceita: (nrrqu, filialId) =>
      apiCall(`/divergencias/receita/${nrrqu}/${filialId}/historico`),
    divergenciaDetalhes: (id) => apiCall(`/divergencias/${id}`),
    divergenciaCriar: (dados) =>
      apiCall('/divergencias', { method: 'POST', body: JSON.stringify(dados) }),
    divergenciaResolver: (id, dados) =>
      apiCall(`/divergencias/${id}/resolver`, { method: 'PUT', body: JSON.stringify(dados) }),
    divergenciaAprovar: (id, dados = {}) =>
      apiCall(`/divergencias/${id}/aprovar`, { method: 'POST', body: JSON.stringify(dados) }),
    divergenciaRejeitar: (id, dados) =>
      apiCall(`/divergencias/${id}/rejeitar`, { method: 'POST', body: JSON.stringify(dados) }),
    divergenciaComentar: (id, mensagem) =>
      apiCall(`/divergencias/${id}/comentar`, { method: 'POST', body: JSON.stringify({ mensagem }) }),
    divergenciasNotificacoes: (filialId, { limit = 20, after = 0 } = {}) => {
      const qp = new URLSearchParams({ filial_id: filialId, limit, after });
      return apiCall(`/divergencias/notificacoes?${qp.toString()}`);
    },
    divergenciasNotificacoesVisualizar: (ids) =>
      apiCall('/divergencias/notificacoes/visualizar', {
        method: 'POST', body: JSON.stringify({ ids })
      }),
    divergenciasNotificacoesMarcarLidas: (ids) =>
      apiCall('/divergencias/notificacoes/marcar-lidas', {
        method: 'POST', body: JSON.stringify({ ids })
      }),

    // ============================================================================
    // SISTEMA
    // ============================================================================
    healthCheck: () => apiCall('/health')
  }), [apiCall, apiCallWithFallback]);
  
  const getNivelNome = (nivel) => {
    const nomes = { 
      1: 'Expedição', 
      2: 'Laboratório', 
      3: 'Administrador' 
    };
    return nomes[nivel] || 'Desconhecido';
  };

  useEffect(() => {
    // Verificar usuário salvo
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch (error) {
        localStorage.removeItem('user');
      }
    }

    // Health check da API
    checkApiHealth();
    setLoading(false);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    concluirTrocaSenha,
    hasPermission, 
    api, 
    NIVEIS, 
    getNivelNome,
    apiStatus,
    checkApiHealth,
    API_BASE_URL // Expor para debug
  // hasPermission fecha sobre `user`, entao `user` precisa estar aqui.
  // login/logout/concluirTrocaSenha/checkApiHealth so usam setters e
  // constantes de modulo — nao ha risco de fechar sobre valor velho.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, loading, apiStatus, api]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
